import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ImageGallery from './components/ImageGallery';
import BusinessInfo from './components/BusinessInfo';
import PricingCard from './components/PricingCard';
import BagContents from './components/BagContents';
import PickupInfo from './components/PickupInfo';
import ReviewsSection from './components/ReviewsSection';
import PurchaseButton from './components/PurchaseButton';

const SurpriseBagDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bagId = searchParams?.get('id') || '1';

  const [walletBalance, setWalletBalance] = useState(125.50);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for the surprise bag
  const bagData = {
    id: bagId,
    title: "Bolsa Sorpresa Premium - Panadería Luna",
    images: [
    {
      url: "https://images.unsplash.com/photo-1612180106801-25245a25b8b3",
      alt: "Variedad de panes artesanales frescos incluyendo baguettes, croissants y pasteles en canasta de mimbre"
    },
    {
      url: "https://images.unsplash.com/photo-1558493754-981b3bca4af7",
      alt: "Selección de pasteles y dulces gourmet con glaseados coloridos y decoraciones elegantes"
    },
    {
      url: "https://images.unsplash.com/photo-1580185988314-58d7a54626ca",
      alt: "Interior acogedor de panadería con estantes llenos de pan fresco y ambiente cálido"
    }],

    business: {
      name: "Panadería Luna",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1352fbc5f-1762504676174.png",
      avatarAlt: "Logo de panadería con diseño artesanal mostrando trigo dorado y horno tradicional",
      rating: 4.7,
      reviewCount: 234,
      certificationLevel: "Plata",
      reliabilityScore: 94,
      responseTime: "< 5 min",
      walkingDistance: "350m",
      walkingTime: "4 min"
    },
    pricing: {
      originalPrice: 280.00,
      discountedPrice: 140.00,
      savings: 140.00,
      availableCount: 3,
      urgencyLevel: "medium",
      isSubsidized: true,
      subsidyAmount: 25.00
    },
    contents: {
      description: `Una selección premium de productos de panadería artesanal que incluye panes frescos del día, pasteles gourmet y dulces tradicionales. Todos nuestros productos se elaboran con ingredientes locales de alta calidad y técnicas tradicionales.`,
      estimatedItems: [
      { name: "Pan artesanal variado", quantity: "3-4 piezas", originalPrice: 85.00 },
      { name: "Pasteles gourmet", quantity: "2-3 piezas", originalPrice: 120.00 },
      { name: "Croissants rellenos", quantity: "2 piezas", originalPrice: 45.00 },
      { name: "Dulces tradicionales", quantity: "Selección", originalPrice: 30.00 }],

      dietaryInfo: {
        isVegetarian: true,
        isVegan: false,
        isGlutenFree: false,
        isHalal: false,
        isKosher: false
      },
      allergens: ["Gluten", "Huevos", "Lácteos", "Frutos secos"],
      qualityStandards: [
      "Productos elaborados el mismo día",
      "Ingredientes locales certificados",
      "Cadena de frío mantenida",
      "Sin productos abiertos o alterados"]

    },
    pickup: {
      pickupWindow: {
        startTime: "18:00",
        endTime: "20:30",
        timeRemaining: "2h 15min",
        urgencyLevel: "medium"
      },
      location: {
        address: "Av. Universidad 1234, Col. Del Valle, Ciudad de México",
        coordinates: { lat: 19.4326, lng: -99.1332 },
        walkingDistance: "350m",
        walkingTime: "4 min",
        landmarks: [
        "Frente a la estación del metro Universidad",
        "Al lado de la librería Porrúa",
        "Edificio azul con letrero dorado"]

      },
      businessHours: {
        openTime: "06:00",
        closeTime: "21:00",
        isOpen: true,
        specialHours: "Horario extendido los fines de semana hasta las 22:00"
      },
      policies: {
        readyOnArrival: true,
        maxWaitTime: "3 minutos",
        cancellationPolicy: "Cancelación gratuita hasta 1 hora antes de la recogida",
        qualityGuarantee: "Garantía de calidad del 100% o reembolso completo"
      }
    },
    reviews: [
    {
      id: 1,
      userName: "María González",
      userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11a93dc89-1762274343125.png",
      userAvatarAlt: "Mujer joven sonriente con cabello castaño y blusa azul en foto profesional",
      rating: 5,
      date: "2024-11-05",
      comment: `¡Increíble calidad y variedad! Los pasteles estaban fresquísimos y el pan artesanal delicioso. Definitivamente vale la pena el precio. El personal fue muy amable durante la recogida.`,
      isVerified: true,
      helpfulCount: 12,
      images: [
      {
        url: "https://images.unsplash.com/photo-1706599760751-886c0e36ad8e",
        alt: "Bolsa de papel con variedad de panes y pasteles frescos sobre mesa de madera"
      }]

    },
    {
      id: 2,
      userName: "Carlos Mendoza",
      userAvatar: "https://images.unsplash.com/photo-1709715357479-591f9971fb05",
      userAvatarAlt: "Hombre profesional con barba y camisa blanca sonriendo en oficina moderna",
      rating: 4,
      date: "2024-11-03",
      comment: "Buena relación calidad-precio. Los croissants estaban perfectos, aunque esperaba un poco más de variedad en los dulces. El proceso de recogida fue muy eficiente.",
      isVerified: true,
      helpfulCount: 8,
      businessResponse: {
        date: "2024-11-04",
        message: "Gracias por tu comentario, Carlos. Tomamos nota sobre la variedad de dulces y trabajaremos en mejorar la selección. ¡Esperamos verte pronto!"
      }
    },
    {
      id: 3,
      userName: "Ana Rodríguez",
      userAvatar: "https://images.unsplash.com/photo-1700680056842-20d9acc36e91",
      userAvatarAlt: "Estudiante universitaria con gafas y suéter verde sonriendo en campus",
      rating: 5,
      date: "2024-11-01",
      comment: "Como estudiante, estas bolsas sorpresa son perfectas para mi presupuesto. La calidad es excelente y me encanta contribuir a reducir el desperdicio de alimentos.",
      isVerified: true,
      helpfulCount: 15
    }]

  };

  const finalPrice = bagData?.pricing?.isSubsidized ?
  bagData?.pricing?.discountedPrice - bagData?.pricing?.subsidyAmount :
  bagData?.pricing?.discountedPrice;

  const handlePurchase = async () => {
    // Simulate purchase process
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Update wallet balance
    setWalletBalance((prev) => prev - finalPrice);

    // Navigate to success page or show success modal
    alert('¡Compra realizada con éxito! Recibirás un código QR para la recogida.');
    navigate('/consumer-dashboard');
  };

  const handleBackClick = () => {
    navigate('/consumer-dashboard');
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="aspect-[4/3] bg-muted rounded-lg"></div>
                <div className="h-32 bg-muted rounded-lg"></div>
              </div>
              <div className="space-y-4">
                <div className="h-48 bg-muted rounded-lg"></div>
                <div className="h-32 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackClick}>

            <Icon name="ArrowLeft" size={20} />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl text-foreground">
              {bagData?.title}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Detalles de la bolsa sorpresa
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Business Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <ImageGallery
              images={bagData?.images}
              businessName={bagData?.business?.name} />


            {/* Business Information */}
            <BusinessInfo business={bagData?.business} />

            {/* Bag Contents */}
            <BagContents
              description={bagData?.contents?.description}
              estimatedItems={bagData?.contents?.estimatedItems}
              dietaryInfo={bagData?.contents?.dietaryInfo}
              allergens={bagData?.contents?.allergens}
              qualityStandards={bagData?.contents?.qualityStandards} />


            {/* Pickup Information */}
            <PickupInfo
              pickupWindow={bagData?.pickup?.pickupWindow}
              location={bagData?.pickup?.location}
              businessHours={bagData?.pickup?.businessHours}
              policies={bagData?.pickup?.policies} />


            {/* Reviews Section */}
            <ReviewsSection
              reviews={bagData?.reviews}
              averageRating={bagData?.business?.rating}
              totalReviews={bagData?.business?.reviewCount} />

          </div>

          {/* Right Column - Pricing and Purchase */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <PricingCard
              originalPrice={bagData?.pricing?.originalPrice}
              discountedPrice={bagData?.pricing?.discountedPrice}
              savings={bagData?.pricing?.savings}
              availableCount={bagData?.pricing?.availableCount}
              urgencyLevel={bagData?.pricing?.urgencyLevel}
              isSubsidized={bagData?.pricing?.isSubsidized}
              subsidyAmount={bagData?.pricing?.subsidyAmount} />


            {/* Purchase Button */}
            <div className="sticky top-24">
              <PurchaseButton
                finalPrice={finalPrice}
                isAvailable={bagData?.pricing?.availableCount > 0}
                walletBalance={walletBalance}
                onPurchase={handlePurchase} />

            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <Button
                variant="outline"
                fullWidth
                iconName="Heart"
                iconPosition="left">

                Guardar en favoritos
              </Button>
              
              <Button
                variant="outline"
                fullWidth
                iconName="Share2"
                iconPosition="left">

                Compartir
              </Button>
            </div>

            {/* Environmental Impact Preview */}
            <div className="bg-success/10 border border-success/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Leaf" size={20} color="var(--color-success)" />
                <h3 className="font-medium text-success">Impacto Ambiental</h3>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Alimentos rescatados:</span>
                  <span className="font-medium text-foreground">~1.2 kg</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Agua ahorrada:</span>
                  <span className="font-medium text-foreground">~15 litros</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">CO₂ evitado:</span>
                  <span className="font-medium text-foreground">~2.3 kg</span>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3 text-success hover:bg-success/10"
                onClick={() => navigate('/impact-tracking')}>

                Ver mi impacto total
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default SurpriseBagDetails;